<template>
  <rest-page :fetch-endpoint="fetchEndpoint" layout="tabbed">
    <div v-if="isCampaignSnapshot" class="snapshot-container">
      <div class="snapshot-wrapper">
        <el-button
          :disabled="!model.snapshotPrevious"
          circle
          type="text"
          @click="generateRoute('previous')"
        >
          <i class="el-icon-arrow-left" />
        </el-button>
        <span>
          <i class="el-icon-time" style="margin-right: 10px;" />
          {{ getDateTime }}
        </span>
        <span>&#9679;</span>
        <el-tooltip
          :disabled="getEditor.length < 15"
          effect="light"
          :content="getEditor"
        >
          <span class="snapshot-editor">
            <i class="el-icon-user" style="margin-right: 10px;" />
            {{ getEditor }}
          </span>
        </el-tooltip>
        <el-button
          :disabled="!model.snapshotNext"
          circle
          type="text"
          @click="generateRoute('next')"
        >
          <i class="el-icon-arrow-right" />
        </el-button>
      </div>
      <el-button
        @click="exitVersioning"
        type="text"
        class="exit-versioning-btn"
      >
        {{ $t('CampaignAuditLog_ExitVersioning_Button') }}
        <i class="el-icon-close" style="font-size: 17px" />
      </el-button>
    </div>
    <div
      v-if="isDeletedSnapshotCampaign"
      class="snapshot-deleted-campaign-banner"
    >
      <icon glyph="bin-2" size="20" style="margin-right: 8px" />
      <span>{{ $t('CampaignAuditLog_DeletedCampaign_Banner') }}</span>
    </div>
    <page-header
      :title="title"
      :tag="tag"
      :breadcrumbs="breadcrumbs"
      id="page-header"
    >
      <template #actions>
        <slot name="pre-actions"></slot>
        <el-button
          v-if="!finalStep && !hideNextButton"
          type="primary"
          @click="nextStep"
          data-qa="next-step-button"
        >
          {{ nextStepButton ? nextStepButton : $t('Button_NextStep') }}
          <i class="el-icon-arrow-right el-icon-right"></i>
        </el-button>
        <el-button
          v-if="finalStep && showSaveButton"
          :disabled="readonly || !stepsCompleted"
          @click="save"
          type="primary"
          data-qa="save-button"
        >
          <i class="ico icon-floppy"></i>
          {{ saveButton ? saveButton : $t('Button_Save') }}
        </el-button>
        <slot name="post-actions"></slot>
      </template>

      <template #content>
        <slot name="sub-header"></slot>
      </template>
    </page-header>

    <page-steps
      ref="stepper"
      @can-continue-updated="canContinueUpdated"
      @next-button-updated="updateNextButton"
      @final-step-updated="updateFinalStep"
      :steps="steps"
      :model="model"
      :recipients="recipients"
      :rules="rules"
      :save-endpoint="saveEndpoint"
      :after-save="afterSave"
      :is-edit="isEdit"
      :readonly="readonly"
      :validation="validation"
      :hash="hash"
      @calculate="samplingAllowed => this.$emit('calculate', samplingAllowed)"
      @check="$emit('check', $event)"
      @checkChanges="$emit('checkChanges', $event)"
      @clear-errors="$emit('clear-errors')"
    />
  </rest-page>
</template>

<script>
import { projectRoute } from '@/core/helpers'
import RestPage from '@/common/components/rest/RestPage'
import PageHeader from '@/common/components/page/PageHeaderNew'
import PageSteps from '@/common/components/page/PageSteps'
import { EventBus } from '@/core/EventBus'
import moment from 'moment/moment'

export default {
  name: 'PageStepped',

  components: {
    RestPage,
    PageHeader,
    PageSteps
  },

  props: {
    title: {
      type: String,
      required: true
    },

    tag: {
      type: Object,
      required: false
    },

    steps: {
      type: Array,
      required: true
    },

    breadcrumbs: {
      type: Array,
      required: false,
      default: () => []
    },

    model: {
      type: Object,
      required: true
    },

    props: {
      type: Object,
      required: false
    },

    fetchEndpoint: {
      type: [Object, Array],
      required: false,
      default: {}
    },

    saveEndpoint: {
      type: Object,
      required: true
    },
    afterSave: {
      type: Function,
      required: false
    },

    rules: {
      type: Object,
      required: true
    },

    isEdit: {
      type: Boolean,
      default: false,
      required: false
    },

    readonly: {
      type: Boolean,
      default: false,
      required: false
    },
    showSaveButton: {
      type: Boolean,
      default: true
    },
    saveButton: {
      type: String,
      required: false
    },

    nextStepButton: {
      type: String,
      required: false
    },

    hideNextButton: {
      type: Boolean,
      default: false
    },
    validation: {
      type: Boolean,
      default: true
    },
    hash: {
      type: Boolean,
      default: false
    },
    recipients: {
      type: Object,
      default: () => {}
    }
  },
  mounted() {
    EventBus.$on('page-stepped-next-step', this.nextStep)
  },
  data() {
    return {
      nextButton: {},
      finalStep: false,
      isAllowedToContinue: false
    }
  },

  methods: {
    nextStep() {
      this.$refs.stepper
        .validate()
        .then(valid => {
          if (valid) {
            this.$refs.stepper.completeStep(
              this.$refs.stepper.currentStep.index
            )
            this.$refs.stepper.proceed({ value: true })
            this.$refs.stepper.nextStep()
          }
        })
        .catch(error => {
          return Object.values(error).map(e => e[0].field)
        })
    },

    updateNextButton(newValue) {
      Object.assign(this.nextButton, newValue)
    },

    updateFinalStep(newValue) {
      this.finalStep = newValue
    },

    canContinueUpdated(newValue) {
      this.isAllowedToContinue = newValue
    },

    save() {
      this.$refs.stepper.save()
    },

    generateRoute(action) {
      const createTime =
        action === 'previous'
          ? this.model.snapshotPrevious.create_time || null
          : this.model.snapshotNext.create_time || null

      this.$router.push(
        projectRoute({
          name: 'ViewCampaignSnapshot',
          query: {
            ...this.$route.query,
            id: this.model.id,
            create_time: createTime
          },
          hash: this.$route.hash
        })
      )

      this.$nextTick(() => {
        EventBus.$emit('force-reload-page')
      })
    },

    exitVersioning() {
      this.$router.push(
        projectRoute({
          name: 'CampaignAuditLog'
        })
      )
    }
  },

  computed: {
    getDateTime() {
      return moment.unix(this.model.create_time).format('YYYY-MM-DD HH:mm')
    },

    getEditor() {
      return this.model.updated_by || ''
    },

    isCampaignSnapshot() {
      return this.$route.name === 'ViewCampaignSnapshot'
    },

    isDeletedSnapshotCampaign() {
      return this.isCampaignSnapshot && this.model.isDeletedCampaign
    },

    stepsCompleted() {
      return this.steps.every(element => element.completed === true)
    },
    currentStep() {
      return this.$refs.stepper.currentStep
    }
  }
}
</script>

<style scoped>
.snapshot-container {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  background-color: #c81d87;
  height: 50px;
  color: white;
}
.snapshot-wrapper {
  font-size: 14px;
  min-width: 500px;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
}
.exit-versioning-btn {
  position: absolute;
  right: 20px;
  border: none;
  color: white;
  font-size: 14px;
  font-weight: normal;
}
.snapshot-container i {
  color: white;
}
.snapshot-wrapper i {
  font-size: 20px;
}
.snapshot-deleted-campaign-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  height: 50px;
  background-color: #ffffff;
}
.snapshot-editor {
  max-width: 130px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>



// WEBPACK FOOTER //
// src/common/components/page/PageStepped.vue